import type { AxiosResponse } from 'axios'
import { call, select, takeLatest } from 'redux-saga/effects'

import { getActiveCompanyId, getErrorMessage, parseApiErrorMessage } from '@helpers'

import type { RejectedFileResponse } from '@oldComponents/FileDropzone'

import actions from './actions'
import * as api from './api'
import type { FetchCompanyDocumentsPayload, RemoveCompanyDocumentPayload, UploadCompanyDocumentsPayload } from './types'

function* fetchCompanyDocumentsSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<FetchCompanyDocumentsPayload>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<BackendCompanyDocumentListResults> = yield call(
      api.fetchCompanyDocuments,
      companyId,
      payload
    )
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* uploadCompanyDocumentsSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<UploadCompanyDocumentsPayload>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const rejected: RejectedFileResponse[] = []

    //* the endpoint takes one document per request, so the files are uploaded one after the other and
    //* the per file errors are collected for the file dropzone instead of failing the whole batch
    //! keep this sequential: the backend reuses the company's open document upload task, and it
    //! looks the task up before creating it, so parallel requests would each create their own
    for (const [fileIndex, file] of payload.files.entries()) {
      try {
        yield call(api.uploadCompanyDocument, companyId, file, function onUploadProgress({ loaded, total }) {
          //* total is missing while the request is still being prepared
          payload.onProgress?.(fileIndex, total ? Math.round((loaded / total) * 100) : 0)
        })
      } catch (error) {
        rejected.push({ filename: file.name, message: parseApiErrorMessage(error) ?? '' })
      }
    }

    yield call(resolve, { rejected })
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* removeCompanyDocumentSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<RemoveCompanyDocumentPayload>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    yield call(api.removeCompanyDocument, companyId, payload)
    yield call(resolve)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

// watcher Saga
export default function* companyDocumentsSaga() {
  yield takeLatest(actions.fetchCompanyDocuments.REQUEST, fetchCompanyDocumentsSaga)
  yield takeLatest(actions.removeCompanyDocument.REQUEST, removeCompanyDocumentSaga)
  yield takeLatest(actions.uploadCompanyDocuments.REQUEST, uploadCompanyDocumentsSaga)
}
